import React, { FC } from 'react';

import { mqMin } from '@lucid/theme';

import { LucidTheme } from '../../../types/types';
import Box from '../../box/box';
import Card from '../../card/card';
import Icon from '../../icon/icon';
import Text from '../../text/text';
import { ComponentImage } from '../../../types/generics';

export interface TemplateCardProps {
  /** The TemplateCard contributor. */
  contributorName?: string | null;
  /** The TemplateCard header. */
  header?: string;
  /** The TemplateCard image. */
  image?: ComponentImage;
  /** Checks if the TemplateCard is the hub link. */
  isTemplateHubLink?: boolean;
  /** The TemplateCard subheader. */
  subheader?: string;
}

const TemplateCard: FC<TemplateCardProps> = ({
  contributorName,
  header,
  image,
  isTemplateHubLink = false,
  subheader,
}) => {
  const templateHubLink = {
    imageAlt: 'Become a contributor.',
    imageSrc:
      'https://cdn-cashy-static-assets.lucidchart.com/lucidspark/marketing/pages/templates/template-hub-image.png',
  };

  return (
    <Card
      shouldAnimateOnHover
      css={(theme: LucidTheme) => [
        {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
        contributorName && {
          borderColor: theme.colors.primary,
        },
        isTemplateHubLink && {
          background: `linear-gradient(180deg, ${theme.colors.primary} 0%, ${theme.colors.primary} 35%, ${theme.colors.primarySecondaryAccent} 35%, ${theme.colors.primarySecondaryAccent} 100%)`,
        },
      ]}
      variant="template"
    >
      <Card.Title
        css={(theme: LucidTheme) => [
          contributorName && {
            marginBottom: theme.space.s16,
          },
          !contributorName && {
            paddingBottom: theme.space.s32,
          },
          isTemplateHubLink && {
            paddingBottom: 0,
            color: theme.colors.white,
          },
        ]}
        variant="headlineL"
      >
        {header}
      </Card.Title>
      {(contributorName || isTemplateHubLink) && (
        <Box
          css={(theme: LucidTheme) => [
            {
              display: 'flex',
              '& > p': {
                margin: 0,
              },
            },
            contributorName && {
              paddingBottom: theme.space.s8,
            },
            isTemplateHubLink && {
              paddingRight: theme.space.s32,
              paddingBottom: theme.space.s40,
            },
          ]}
        >
          {!isTemplateHubLink && (
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.primary,
                fontWeight: theme.fontWeights.stronger,
              })}
              variant="labelS400"
            >
              Made by&nbsp;
            </Text>
          )}
          {contributorName && !isTemplateHubLink && (
            <Card.Subtitle isText variant="paragraphS400">
              {contributorName}
            </Card.Subtitle>
          )}
          {isTemplateHubLink && (
            <Card.Subtitle
              isText
              css={(theme: LucidTheme) => ({
                color: theme.colors.white,
              })}
              variant="paragraphS300"
            >
              {subheader}
              <Icon
                color="white"
                css={(theme: LucidTheme) => ({
                  top: '4px',
                  paddingLeft: theme.space.s16,
                })}
                type="ArrowRightLong"
              />
            </Card.Subtitle>
          )}
        </Box>
      )}
      <Card.Image
        alt={isTemplateHubLink ? templateHubLink.imageAlt : image?.imageAlt!}
        aspectRatio="4:3"
        css={[
          {
            '& > .image': {
              [mqMin.tabletSmall]: {
                maxHeight: '250px',
              },
            },
          },
          isTemplateHubLink && {
            overflow: 'unset',
          },
        ]}
        src={isTemplateHubLink ? templateHubLink.imageSrc : image?.imageSrc!}
      />
    </Card>
  );
};

export { TemplateCard };
export default TemplateCard;
